import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  AlertTriangle,
  CheckCircle,
  Trash2,
  RefreshCw,
  DollarSign,
  FileWarning,
  Loader2,
  ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupplierDataCleanup = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [newOpeningBalance, setNewOpeningBalance] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (open) {
      analyzeData();
    }
  }, [open]);

  const analyzeData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/suppliers/data-cleanup/analyze`);
      setAnalysisData(response.data);
    } catch (error) {
      console.error("Error analyzing data:", error);
      toast.error("Failed to analyze supplier data");
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async (supplierId, action) => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/suppliers/${supplierId}/data-cleanup/clear-unallocated`,
        { action }
      );
      toast.success(response.data.message);
      analyzeData(); // Refresh
      setSelectedSupplier(null);
    } catch (error) {
      console.error("Error cleaning up:", error);
      toast.error(error.response?.data?.detail || "Failed to clean up");
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateOpeningBalance = async (supplierId) => {
    if (!newOpeningBalance) {
      toast.error("Please enter a new opening balance");
      return;
    }
    setProcessing(true);
    try {
      const response = await axios.put(
        `${API}/suppliers/${supplierId}/opening-balance`,
        { opening_balance: parseFloat(newOpeningBalance) }
      );
      toast.success(response.data.message);
      setNewOpeningBalance("");
      analyzeData();
      setSelectedSupplier(null);
    } catch (error) {
      console.error("Error updating balance:", error);
      toast.error(error.response?.data?.detail || "Failed to update balance");
    } finally {
      setProcessing(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "HIGH":
        return "bg-red-100 text-red-800 border-red-300";
      case "MEDIUM":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "LOW":
        return "bg-blue-100 text-blue-800 border-blue-300";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatCurrency = (value) => {
    return `R ${(value || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileWarning className="h-5 w-5 text-orange-500" />
            Supplier Data Cleanup Utility
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <span className="ml-2">Analyzing supplier data...</span>
          </div>
        ) : analysisData ? (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-3">
              <Card className="bg-blue-50">
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-blue-700">
                    {analysisData.summary.total_suppliers}
                  </div>
                  <div className="text-xs text-blue-600">Total Suppliers</div>
                </CardContent>
              </Card>
              <Card className="bg-orange-50">
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-orange-700">
                    {analysisData.summary.suppliers_with_issues}
                  </div>
                  <div className="text-xs text-orange-600">With Issues</div>
                </CardContent>
              </Card>
              <Card className="bg-red-50">
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-red-700">
                    {formatCurrency(analysisData.summary.total_unallocated)}
                  </div>
                  <div className="text-xs text-red-600">Total Unallocated</div>
                </CardContent>
              </Card>
              <Card className="bg-purple-50">
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-purple-700">
                    {formatCurrency(analysisData.summary.total_orphaned_payments)}
                  </div>
                  <div className="text-xs text-purple-600">Orphaned Payments</div>
                </CardContent>
              </Card>
            </div>

            {/* Refresh Button */}
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={analyzeData}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Analysis
              </Button>
            </div>

            {/* Issues List */}
            {analysisData.issues.length === 0 ? (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-6 text-center">
                  <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
                  <h3 className="font-semibold text-green-700">All Clear!</h3>
                  <p className="text-sm text-green-600">
                    No data discrepancies found in supplier accounts.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-700">
                  Suppliers with Data Issues ({analysisData.issues.length})
                </h3>
                {analysisData.issues.map((supplier) => (
                  <Card
                    key={supplier.supplier_id}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      selectedSupplier?.supplier_id === supplier.supplier_id
                        ? "ring-2 ring-blue-500"
                        : ""
                    }`}
                    onClick={() => setSelectedSupplier(supplier)}
                  >
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold">{supplier.supplier_name}</h4>
                          <p className="text-xs text-gray-500">
                            #{supplier.supplier_number}
                          </p>
                        </div>
                        <div className="text-right text-sm">
                          <div>
                            Invoices: {formatCurrency(supplier.total_invoices)}
                          </div>
                          <div>
                            Payments: {formatCurrency(supplier.total_payments)}
                          </div>
                          <div className={supplier.calculated_balance < 0 ? "text-green-600" : "text-red-600"}>
                            Balance: {formatCurrency(supplier.calculated_balance)}
                          </div>
                        </div>
                      </div>

                      {/* Issues */}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {supplier.issues.map((issue, idx) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className={getSeverityColor(issue.severity)}
                          >
                            {issue.type === "UNALLOCATED_PAYMENTS" && (
                              <DollarSign className="h-3 w-3 mr-1" />
                            )}
                            {issue.type === "ORPHANED_PAYMENTS" && (
                              <AlertTriangle className="h-3 w-3 mr-1" />
                            )}
                            {issue.message}
                          </Badge>
                        ))}
                      </div>

                      {/* Cleanup Actions (when selected) */}
                      {selectedSupplier?.supplier_id === supplier.supplier_id && (
                        <div className="mt-4 pt-4 border-t space-y-3">
                          <h5 className="font-medium text-sm">Cleanup Options:</h5>
                          
                          {/* Show unallocated details */}
                          {supplier.unallocated > 0 && (
                            <div className="bg-yellow-50 p-3 rounded-lg text-sm">
                              <div className="font-medium text-yellow-800 mb-2">
                                Unallocated Amount: {formatCurrency(supplier.unallocated)}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCleanup(supplier.supplier_id, "apply_to_opening");
                                  }}
                                  disabled={processing}
                                >
                                  {processing ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                  ) : (
                                    <ArrowRight className="h-4 w-4 mr-1" />
                                  )}
                                  Apply to Opening Balance
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-300"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm("Are you sure you want to delete orphaned payment records? This cannot be undone.")) {
                                      handleCleanup(supplier.supplier_id, "delete_orphaned");
                                    }
                                  }}
                                  disabled={processing}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Delete Orphaned Records
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Manual opening balance adjustment */}
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <Label className="text-sm font-medium text-blue-800">
                              Manually Set Opening Balance
                            </Label>
                            <p className="text-xs text-blue-600 mb-2">
                              Current: {formatCurrency(supplier.opening_balance)}
                            </p>
                            <div className="flex gap-2">
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="New opening balance"
                                value={newOpeningBalance}
                                onChange={(e) => setNewOpeningBalance(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                className="w-40"
                              />
                              <Button
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateOpeningBalance(supplier.supplier_id);
                                }}
                                disabled={processing || !newOpeningBalance}
                              >
                                {processing ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  "Update"
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {analysisData.total_issues_found > 50 && (
              <p className="text-sm text-gray-500 text-center">
                Showing top 50 of {analysisData.total_issues_found} suppliers with issues
              </p>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default SupplierDataCleanup;
